/*
 * Test 3.6 — Pre-Filter-Chips (Trello 119b)
 *
 * <filter-chips> filters the All-products collection by a product tag without reloading the page.
 * Clicking a chip re-fetches the collection filtered by `/collections/all/<tag>` via the Section
 * Rendering API, swaps the product grid + count in place, then re-runs the theme's existing
 * `loadProducts()` (re-init Load-more pagination on the filtered set) and `updateProductsGrid()`
 * (re-apply the model/plain view). This is the same swap-and-reinit pattern facets.js uses.
 *
 * Only one chip is active at a time; clicking the active chip again clears the filter.
 * A tag with no products 301-redirects to the unfiltered collection — we detect that and render an
 * empty state instead of silently showing every product.
 */
if (!customElements.get('filter-chips')) {
  customElements.define(
    'filter-chips',
    class FilterChips extends HTMLElement {
      constructor() {
        super();
        this.baseUrl = this.dataset.baseUrl;
        this.chips = Array.from(this.querySelectorAll('.filter-chips__chip'));
        this.activeChip = null;
        this.isLoading = false;

        this.chips.forEach((chip) => {
          chip.addEventListener('click', () => this.onChipClick(chip));
        });

        // Reflect the current URL: on a direct load / reload / shared link of a tag view
        // (e.g. /collections/all/sale), highlight the matching chip. The grid + facets are already
        // server-rendered for that tag, so this is purely a visual sync — no re-fetch needed.
        this.syncActiveFromUrl();
      }

      syncActiveFromUrl() {
        const current = this.normalizePath(window.location.href);
        const chip = this.chips.find((c) => this.normalizePath(c.dataset.filterUrl) === current);
        if (chip) this.setActive(chip);
      }

      onChipClick(chip) {
        if (this.isLoading) return;

        const isClear = chip === this.activeChip;
        const targetChip = isClear ? null : chip;
        // Whether selecting a tag or clearing it, keep any active facet filters / sort (?filter...=,
        // ?sort_by=) — only the tag segment of the path changes, so the two filter mechanisms compose.
        const url = this.withCurrentParams(isClear ? this.baseUrl : chip.dataset.filterUrl);
        const tagHandle = isClear ? null : this.lastSegment(chip.dataset.filterUrl);

        this.setActive(targetChip);
        this.applyFilter(url, isClear, tagHandle);
      }

      normalizePath(href) {
        return new URL(href, window.location.origin).pathname.replace(/\/+$/, '');
      }

      lastSegment(href) {
        return this.normalizePath(href).split('/').pop();
      }

      // Carry the page's current query string (facet filters, sort_by, …) over to a chip/base URL,
      // dropping `page` since the tag change resets pagination to the first page.
      withCurrentParams(href) {
        const url = new URL(href, window.location.origin);
        new URLSearchParams(window.location.search).forEach((value, key) => {
          if (key !== 'page') url.searchParams.append(key, value);
        });
        return url.pathname + url.search;
      }

      setActive(chip) {
        this.activeChip = chip;
        this.chips.forEach((c) => {
          c.classList.toggle('filter-chips__chip--active', c === chip);
          c.setAttribute('aria-pressed', c === chip ? 'true' : 'false');
        });
      }

      get loadingTarget() {
        const container = document.getElementById('ProductGridContainer');
        return container ? container.querySelector('.collection') : null;
      }

      async applyFilter(url, isClear, tagHandle) {
        const ajaxinate = document.getElementById('AjaxinateContainer');
        if (!ajaxinate) {
          // No AJAX grid present (unexpected) — fall back to a normal navigation.
          window.location.href = url;
          return;
        }

        const sectionId = ajaxinate.dataset.id;
        const loadingEl = this.loadingTarget;
        this.isLoading = true;
        if (loadingEl) loadingEl.classList.add('loading');

        try {
          const separator = url.indexOf('?') === -1 ? '?' : '&';
          const res = await fetch(`${url}${separator}section_id=${sectionId}`);

          // Empty-state guard: a tag with 0 products redirects to the unfiltered collection,
          // dropping the tag segment from the URL. (A pure locale redirect keeps the tag, so we
          // check the final path rather than just res.redirected to avoid false positives.)
          const tagStripped =
            !isClear &&
            tagHandle &&
            !new URL(res.url).pathname.replace(/\/+$/, '').endsWith('/' + tagHandle);

          if (tagStripped) {
            this.renderEmpty(ajaxinate);
          } else {
            const text = await res.text();
            const doc = new DOMParser().parseFromString(text, 'text/html');
            const newAjaxinate = doc.getElementById('AjaxinateContainer');
            if (newAjaxinate) ajaxinate.innerHTML = newAjaxinate.innerHTML;
            const newCount = doc.getElementById('ProductCount');
            this.updateCount(newCount ? newCount.innerHTML : null);

            // The available facets depend on the filtered set, so refresh the filter drawer too.
            // The fetched section (main-collection-product-grid) contains the facets markup, so we
            // hand it to facets.js's own renderer — the same call its popstate handler uses (event
            // = null), which swaps every .js-filter, active facets, counts and the mobile open row.
            if (typeof FacetFiltersForm !== 'undefined') FacetFiltersForm.renderFilters(text, null);
          }

          // Reflect the active tag in the address bar (shareable / bookmarkable) and drop it on
          // clear. replaceState — not pushState — because facets.js owns a popstate handler on this
          // same page; adding history entries here would make it re-render the grid on back/forward.
          this.updateUrl(url);

          // Re-init Load-more pagination + re-apply the model/plain view to the swapped cards.
          if (typeof loadProducts === 'function') loadProducts();
          if (typeof updateProductsGrid === 'function') updateProductsGrid();
        } catch (e) {
          console.error('filter-chips: failed to filter, falling back to navigation', e);
          window.location.href = url;
          return;
        } finally {
          this.isLoading = false;
          if (loadingEl) loadingEl.classList.remove('loading');
        }
      }

      renderEmpty(ajaxinate) {
        const emptyText = this.dataset.emptyText || '';
        // Keep the hidden pagination inputs so loadProducts() can read them without throwing.
        ajaxinate.innerHTML =
          '<input type="hidden" value="1" data-current-page>' +
          '<input type="hidden" value="1" data-total-pages>' +
          '<ul id="product-grid" class="grid product-grid grid--2-col-tablet-down grid--4-col-desktop"></ul>' +
          '<div class="collection collection--empty page-width">' +
          '<div class="title-wrapper center">' +
          '<h2 class="title title--primary">' +
          emptyText +
          '</h2></div></div>';
        this.updateCount(this.dataset.emptyCount || null);
      }

      updateUrl(url) {
        const { pathname, search } = new URL(url, window.location.origin);
        history.replaceState({ filterChips: true }, '', pathname + search);
      }

      updateCount(html) {
        if (html == null) return;
        ['ProductCount', 'ProductCountDesktop'].forEach((id) => {
          const el = document.getElementById(id);
          if (el) el.innerHTML = html;
        });
      }
    }
  );
}
